import React, { useState } from "react";
import { getApiErrorMessage } from "../../utils/apiError";
import { DEFAULT_RADIUS, safeMapUrl } from "../../utils/locationFields";

/**
 * A place for check-ins: find coordinates from the address (HR picks a result
 * and confirms it on the map) or type them. A failed lookup never stops the
 * form from saving. values / onChange: latitude, longitude, geofenceRadius.
 * geocode: the lookup mutation of the screen. Render inside a Bootstrap row.
 */
const LocationFields = ({
  address,
  values,
  onChange,
  geocode,
  findLabel = "Find location",
  minRadius = 10,
  maxRadius = 50000,
  hint,
}) => {
  const [results, setResults] = useState(null); // null = not searched yet
  const [failed, setFailed] = useState("");

  const find = () => {
    setFailed("");
    setResults(null);
    geocode.mutate((address || "").trim(), {
      onSuccess: (res) => {
        setResults(res?.results || []);
        if (res?.failed)
          setFailed("The lookup is unavailable right now. Try again later.");
      },
      onError: (err) =>
        setFailed(getApiErrorMessage(err, "Could not look up the address.")),
    });
  };

  const pick = (r) => {
    onChange({ latitude: String(r.latitude), longitude: String(r.longitude) });
    setResults(null);
  };

  return (
    <>
      <div className="col-12">
        <div className="d-flex flex-wrap align-items-center gap-2">
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={find}
            disabled={geocode.isPending || !(address || "").trim()}
            title={
              (address || "").trim() ? undefined : "Enter the address first"
            }
          >
            {geocode.isPending ? (
              <span
                className="spinner-border spinner-border-sm me-1"
                aria-hidden="true"
              ></span>
            ) : (
              <i className="bi bi-geo-alt me-1"></i>
            )}
            {findLabel}
          </button>
          <span className="fs-070 text-slate-500">
            Address data © OpenStreetMap contributors
          </span>
        </div>
        {failed && (
          <div className="fs-075 text-warning-emphasis mt-2" role="status">
            {failed} You can still type the coordinates or save without them.
          </div>
        )}
        {results && results.length === 0 && !failed && (
          <div className="fs-075 text-slate-600 mt-2" role="status">
            No results for this address. You can type the coordinates or save
            without them.
          </div>
        )}
        {results && results.length > 0 && (
          <ul className="list-group mt-2 fs-080">
            {results.map((r) => {
              const url = safeMapUrl(r.mapUrl);
              return (
                <li
                  key={`${r.latitude},${r.longitude}`}
                  className="list-group-item d-flex flex-wrap justify-content-between align-items-center gap-2"
                >
                  <span className="flex-grow-1 min-w-0">{r.label}</span>
                  <span className="d-inline-flex gap-2 align-items-center">
                    {url && (
                      <a href={url} target="_blank" rel="noreferrer">
                        View on map
                      </a>
                    )}
                    <button
                      type="button"
                      className="btn btn-sm btn-primary"
                      onClick={() => pick(r)}
                    >
                      Use this
                    </button>
                  </span>
                </li>
              );
            })}
          </ul>
        )}
        {results && results.length > 0 && (
          <div className="fs-070 text-slate-500 mt-1">
            Results can be inexact: check the map before using one.
          </div>
        )}
      </div>
      <div className="col-md-4">
        <label htmlFor="client-latitude" className="profile-label">
          Latitude
        </label>
        <input
          id="client-latitude"
          type="number"
          step="any"
          min="-90"
          max="90"
          className="form-control"
          value={values.latitude}
          onChange={(e) => onChange({ latitude: e.target.value })}
        />
      </div>
      <div className="col-md-4">
        <label htmlFor="client-longitude" className="profile-label">
          Longitude
        </label>
        <input
          id="client-longitude"
          type="number"
          step="any"
          min="-180"
          max="180"
          className="form-control"
          value={values.longitude}
          onChange={(e) => onChange({ longitude: e.target.value })}
        />
      </div>
      <div className="col-md-4">
        <label htmlFor="client-radius" className="profile-label">
          Radius (metres)
        </label>
        <input
          id="client-radius"
          type="number"
          step="1"
          min={minRadius}
          max={maxRadius}
          className="form-control"
          placeholder={String(DEFAULT_RADIUS)}
          value={values.geofenceRadius}
          onChange={(e) => onChange({ geofenceRadius: e.target.value })}
        />
      </div>
      {hint && <div className="col-12 fs-070 text-slate-500">{hint}</div>}
    </>
  );
};

export default LocationFields;
